import { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ProgressMonitor from '../components/ProgressMonitor';
import BriefContentEditorModal from '../components/BriefContentEditorModal';
import { api } from '../services/api';

interface BriefContentPage {
  id: number;
  job_id: number;
  title: string;
  url: string | null;
  word_count: number | null;
  qa_score: number | null;
  qa_passed: boolean;
  qa_errors: string[] | null;
  retry_count: number;
  status: string;
  is_edited: boolean;
  edited_at: string | null;
  edited_by_name: string | null;
  generated_at: string | null;
  processing_time_seconds: number | null;
}

interface BriefContentResults {
  job: any;
  pages: BriefContentPage[];
  total_pages: number;
  by_status: Record<string, number>;
  qa_summary: {
    total_pages: number;
    qa_passed: number;
    qa_failed: number;
    average_score: number;
  };
  generation_stats: {
    total_time_seconds: number;
    average_time_seconds: number;
    generated_count: number;
    pending_count: number;
    failed_count: number;
  };
}

interface RecentJob {
  id: number;
  project_id: number;
  status: string;
  completed_at: string | null;
  created_at: string;
  settings: Record<string, any> | null;
}

interface FactsAgentJob {
  id: number;
  created_at: string | null;
  completed_at: string | null;
  settings: Record<string, any> | null;
  fact_count: number;
}

interface PageEntry {
  title: string;
  url: string;
}

type InputMode = 'manual' | 'bulk';
type TabMode = 'input' | 'prompts';
type FactsSource = 'none' | 'file' | 'factsagent';

export default function BriefContentPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');
  const urlJobId = searchParams.get('job_id');

  const [step, setStep] = useState<'input' | 'configure' | 'processing' | 'results'>('input');
  const [jobId, setJobId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingJob, setLoadingJob] = useState(false);

  // Input state
  const [briefContent, setBriefContent] = useState('');
  const [exampleOutput, setExampleOutput] = useState('');
  const [factsSource, setFactsSource] = useState<FactsSource>('none');
  const [factsFile, setFactsFile] = useState<File | null>(null);
  const [factsAgentJobs, setFactsAgentJobs] = useState<FactsAgentJob[]>([]);
  const [selectedFactsJobId, setSelectedFactsJobId] = useState<number | null>(null);
  const [loadingFactsJobs, setLoadingFactsJobs] = useState(false);
  const [pages, setPages] = useState<PageEntry[]>([{ title: '', url: '' }]);
  const [inputMode, setInputMode] = useState<InputMode>('manual');
  const [bulkTitles, setBulkTitles] = useState('');
  const [bulkUrls, setBulkUrls] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tabMode, setTabMode] = useState<TabMode>('input');

  // Prompt editing state
  const [prompts, setPrompts] = useState<Record<string, string>>({});
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<string | null>(null);
  const [promptDraft, setPromptDraft] = useState('');

  // Generation settings
  const [maxThreads, setMaxThreads] = useState(4);
  const [qaThreshold, setQaThreshold] = useState(94);
  const [maxRetries, setMaxRetries] = useState(2);

  // Results state
  const [results, setResults] = useState<BriefContentResults | null>(null);
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);
  const [, setLoadingJobs] = useState(false);

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<BriefContentPage | null>(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Load recent jobs and facts agent jobs
  useEffect(() => {
    if (projectId) {
      loadRecentJobs();
      loadFactsAgentJobs();
    }
  }, [projectId]);

  const loadFactsAgentJobs = async () => {
    if (!projectId) return;

    try {
      setLoadingFactsJobs(true);
      const response = await api.get(`/factsagent/project/${projectId}/completed-jobs`);
      setFactsAgentJobs(response.data || []);
    } catch (err) {
      console.error('Failed to load Facts Agent jobs:', err);
      setFactsAgentJobs([]);
    } finally {
      setLoadingFactsJobs(false);
    }
  };

  // Load specific job if job_id is in URL
  useEffect(() => {
    if (urlJobId && projectId) {
      loadSpecificJob(parseInt(urlJobId));
    }
  }, [urlJobId, projectId]);

  const loadSpecificJob = async (id: number) => {
    try {
      setLoadingJob(true);
      setError(null);

      // Get job details
      const jobResponse = await api.get(`/jobs/${id}`);
      const job = jobResponse.data;

      setJobId(id);

      // Navigate to appropriate step based on job status
      if (job.status === 'processing' || job.status === 'pending') {
        setStep('processing');
      } else if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
        // Load results
        try {
          const resultsResponse = await api.get(`/briefcontent/results/${id}`);
          setResults(resultsResponse.data);
          setStep('results');
        } catch (err) {
          console.error('Failed to load results:', err);
          setStep('results');
        }
      }
    } catch (err: any) {
      console.error('Failed to load job:', err);
      setError(err.response?.data?.detail || 'Failed to load job');
    } finally {
      setLoadingJob(false);
    }
  };

  const loadRecentJobs = async () => {
    if (!projectId) return;

    try {
      setLoadingJobs(true);
      const response = await api.get(`/projects/${projectId}/jobs?system_type=briefcontent&status=completed&limit=10`);
      setRecentJobs(response.data || []);
    } catch (err) {
      console.error('Failed to load recent jobs:', err);
    } finally {
      setLoadingJobs(false);
    }
  };

  const viewJob = async (job: RecentJob) => {
    try {
      setJobId(job.id);
      const response = await api.get(`/briefcontent/results/${job.id}`);
      setResults(response.data);
      setStep('results');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load job results');
    }
  };

  const addPage = () => {
    setPages([...pages, { title: '', url: '' }]);
  };

  const removePage = (index: number) => {
    if (pages.length > 1) {
      setPages(pages.filter((_, i) => i !== index));
    }
  };

  const updatePage = (index: number, field: 'title' | 'url', value: string) => {
    const newPages = [...pages];
    newPages[index][field] = value;
    setPages(newPages);
  };

  const parseBulkPaste = () => {
    const titleLines = bulkTitles.trim().split('\n').map(s => s.trim()).filter(Boolean);
    const urlLines = bulkUrls.trim().split('\n').map(s => s.trim()).filter(Boolean);

    if (titleLines.length === 0) {
      setError('Please enter at least one title.');
      return;
    }

    const parsedPages: PageEntry[] = titleLines.map((title, index) => ({
      title,
      url: urlLines[index] || ''
    }));

    if (parsedPages.length > 0) {
      setPages(parsedPages);
      setInputMode('manual');
      setBulkTitles('');
      setBulkUrls('');
      setError(null);
    }
  };

  // Load prompts
  const loadPrompts = async () => {
    if (!projectId) return;

    try {
      setLoadingPrompts(true);
      // First try to get project-specific prompts
      const projectResponse = await api.get(`/prompts?system_type=briefcontent&project_id=${projectId}`);

      if (projectResponse.data && projectResponse.data.length > 0) {
        const promptData: Record<string, string> = {};
        for (const p of projectResponse.data) {
          promptData[p.prompt_name] = p.prompt_text;
        }
        setPrompts(promptData);
      } else {
        // Fall back to default prompts
        const defaultResponse = await api.get('/prompts/defaults/briefcontent');
        const promptData: Record<string, string> = {};
        if (defaultResponse.data && defaultResponse.data.prompts) {
          for (const p of defaultResponse.data.prompts) {
            promptData[p.name] = p.text;
          }
        }
        setPrompts(promptData);
      }
    } catch (err) {
      console.error('Failed to load prompts:', err);
      // Try loading defaults on error
      try {
        const defaultResponse = await api.get('/prompts/defaults/briefcontent');
        const promptData: Record<string, string> = {};
        if (defaultResponse.data && defaultResponse.data.prompts) {
          for (const p of defaultResponse.data.prompts) {
            promptData[p.name] = p.text;
          }
        }
        setPrompts(promptData);
      } catch (defaultErr) {
        console.error('Failed to load default prompts:', defaultErr);
      }
    } finally {
      setLoadingPrompts(false);
    }
  };

  // Save prompt
  const savePrompt = async (promptName: string) => {
    if (!projectId) return;

    try {
      await api.post(`/prompts?project_id=${projectId}`, {
        system_type: 'briefcontent',
        prompt_name: promptName,
        prompt_text: promptDraft
      });
      setPrompts({ ...prompts, [promptName]: promptDraft });
      setEditingPrompt(null);
      setPromptDraft('');
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save prompt');
    }
  };

  // Load prompts when switching to prompts tab
  useEffect(() => {
    if (tabMode === 'prompts' && Object.keys(prompts).length === 0) {
      loadPrompts();
    }
  }, [tabMode]);

  const handleSubmit = async () => {
    if (!projectId) {
      setError('No project ID provided. Please select a project first.');
      return;
    }

    if (!briefContent.trim()) {
      setError('Please enter the writing brief.');
      return;
    }

    const validPages = pages.filter(p => p.title.trim());
    if (validPages.length === 0) {
      setError('Please add at least one page with a title.');
      return;
    }

    try {
      setError(null);
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('brief_content', briefContent);
      formData.append('pages_data', JSON.stringify(validPages));
      if (exampleOutput.trim()) {
        formData.append('example_output', exampleOutput);
      }
      // Handle facts based on source selection
      if (factsSource === 'file' && factsFile) {
        formData.append('facts_file', factsFile);
      } else if (factsSource === 'factsagent' && selectedFactsJobId) {
        formData.append('facts_job_id', selectedFactsJobId.toString());
      }

      const response = await api.post('/briefcontent/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setJobId(response.data.job_id);
      setStep('configure');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map((d: any) => d.msg || d).join(', ')
        : err.message || 'Failed to upload brief';
      setError(`Failed to upload brief: ${msg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!jobId) {
      setError('No job found. Please go back and upload your brief again.');
      return;
    }

    try {
      setError(null);
      setIsGenerating(true);
      await api.post(`/briefcontent/generate/${jobId}`, {
        max_threads: maxThreads,
        qa_threshold: qaThreshold,
        max_retries: maxRetries
      });
      setStep('processing');
    } catch (err: any) {
      const detail = err.response?.data?.detail || '';
      // If job is already processing or completed, navigate to appropriate step
      if (detail.includes('already processing')) {
        setStep('processing');
        return;
      }
      if (detail.includes('already completed') || detail.includes('already failed') || detail.includes('already cancelled')) {
        try {
          const response = await api.get(`/briefcontent/results/${jobId}`);
          setResults(response.data);
          setStep('results');
        } catch {
          setError(detail || 'Failed to start generation');
        }
        return;
      }
      setError(detail || 'Failed to start generation');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReset = () => {
    setStep('input');
    setJobId(null);
    setError(null);
    setBriefContent('');
    setExampleOutput('');
    setFactsSource('none');
    setFactsFile(null);
    setSelectedFactsJobId(null);
    setPages([{ title: '', url: '' }]);
    setResults(null);
    setStatusFilter(null);
    loadRecentJobs();
    loadFactsAgentJobs();
  };

  const handleDelete = async () => {
    if (!jobId) return;

    if (!confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      await api.delete(`/briefcontent/${jobId}`);
      handleReset();
    } catch (err: any) {
      alert('Failed to delete job: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRegenerate = async (pageId: number) => {
    try {
      await api.post(`/briefcontent/pages/${pageId}/regenerate`);
      // Refresh results
      if (jobId) {
        const response = await api.get(`/briefcontent/results/${jobId}`);
        setResults(response.data);
      }
    } catch (err: any) {
      alert('Failed to regenerate: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openEditor = (page: BriefContentPage) => {
    setEditingPage(page);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingPage(null);
  };

  const handleEditorSave = async () => {
    if (jobId) {
      try {
        const response = await api.get(`/briefcontent/results/${jobId}`);
        setResults(response.data);
      } catch (err) {
        console.error('Failed to refresh results:', err);
      }
    }
  };

  // Filter pages
  const filteredPages = statusFilter
    ? results?.pages.filter(p => p.status === statusFilter) || []
    : results?.pages || [];

  const validPagesCount = pages.filter(p => p.title.trim()).length;

  // Show loading when loading a specific job from URL
  if (loadingJob) {
    return (
      <Layout projectId={projectId ? parseInt(projectId) : null}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="spinner text-brand-600 w-8 h-8 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading job...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout projectId={projectId ? parseInt(projectId) : null}>
      <div className="animate-fade-in">
        {/* Page Header */}
        <div className="page-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">Bulk Brief to Content</h1>
              <p className="page-subtitle">Generate publication-ready content at scale from writing briefs with AI quality validation
                <a href="https://drive.google.com/file/d/17_dn68m-O3zlCMxQ_6-qgNKqLPQS5olD/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="ml-3 inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>
                  Video Tutorial
                </a>
              </p>
            </div>
            {step === 'results' && (
              <div className="flex items-center gap-3">
                <a
                  href={`/api/briefcontent/${jobId}/download-all?token=${localStorage.getItem('auth_token') || ''}`}
                  download
                  className="btn-primary"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download All (MD)
                </a>
                <button onClick={handleReset} className="btn-secondary">
                  New Job
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-danger-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-danger-700 flex-1">{error}</span>
            <button onClick={() => setError(null)} className="text-danger-400 hover:text-danger-600 flex-shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Recent Jobs Section */}
        {recentJobs.length > 0 && step === 'input' && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Jobs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentJobs.map((job) => (
                <div
                  key={job.id}
                  onClick={() => viewJob(job)}
                  className="card hover:shadow-lg hover:border-brand-200 transition-all cursor-pointer group"
                >
                  <div className="card-body">
                    <div className="flex items-start space-x-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 group-hover:text-brand-600 transition-colors">
                          Job #{job.id}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {job.settings?.total_pages || 0} pages
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(job.completed_at || job.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <span className="text-brand-600 text-sm font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                        View Results
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">Or create a new job below</p>
            </div>
          </div>
        )}

        {/* Input Step */}
        {step === 'input' && (
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Tab Navigation */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setTabMode('input')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  tabMode === 'input'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Content Input
              </button>
              <button
                onClick={() => setTabMode('prompts')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  tabMode === 'prompts'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Edit Prompts
              </button>
            </div>

            {/* Prompts Tab */}
            {tabMode === 'prompts' && (
              <div className="card">
                <div className="card-body">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Edit Generation Prompts</h2>
                      <p className="text-gray-500">Customize the AI prompts used for content generation and QA validation</p>
                    </div>
                  </div>

                  {loadingPrompts ? (
                    <div className="flex items-center justify-center py-8">
                      <span className="spinner mr-2"></span>
                      <span className="text-gray-500">Loading prompts...</span>
                    </div>
                  ) : Object.keys(prompts).length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No prompts configured yet. Default prompts will be used.</p>
                      <button onClick={loadPrompts} className="btn-secondary mt-4">
                        Load Default Prompts
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(prompts).map(([name, text]) => (
                        <div key={name} className="border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
                            <div>
                              <h3 className="font-semibold text-gray-900">{name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
                              <p className="text-xs text-gray-500">{text.length} characters</p>
                            </div>
                            <button
                              onClick={() => {
                                setEditingPrompt(name);
                                setPromptDraft(text);
                              }}
                              className="btn-secondary btn-sm"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit
                            </button>
                          </div>
                          {editingPrompt === name ? (
                            <div className="p-4 space-y-4">
                              <textarea
                                value={promptDraft}
                                onChange={(e) => setPromptDraft(e.target.value)}
                                className="textarea h-64 font-mono text-sm"
                              />
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => {
                                    setEditingPrompt(null);
                                    setPromptDraft('');
                                  }}
                                  className="btn-secondary btn-sm"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => savePrompt(name)}
                                  className="btn-primary btn-sm"
                                >
                                  Save Changes
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="p-4">
                              <pre className="text-xs text-gray-600 whitespace-pre-wrap max-h-32 overflow-y-auto">
                                {text.substring(0, 500)}{text.length > 500 ? '...' : ''}
                              </pre>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Content Input Tab */}
            {tabMode === 'input' && (
            <>
            {/* Writing Brief Input */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Writing Brief</h2>
                    <p className="text-gray-500">Paste your detailed content writing guide</p>
                  </div>
                </div>

                <div>
                  <label className="label">
                    Writing Brief / Guide <span className="text-danger-500">*</span>
                  </label>
                  <textarea
                    value={briefContent}
                    onChange={(e) => setBriefContent(e.target.value)}
                    placeholder="Paste your detailed writing brief here...

This should include:
- Content structure requirements
- H1/H2/H3 templates
- Section-by-section instructions
- Word count requirements
- Formatting guidelines
- Bullet point requirements
- FAQ requirements
- etc."
                    className="textarea h-64 font-mono text-sm"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            {/* Example Output (Optional) */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Example Output</h2>
                    <p className="text-gray-500">Optional: Provide a sample for style reference</p>
                  </div>
                </div>

                <div>
                  <label className="label">
                    Example Content (Optional)
                  </label>
                  <textarea
                    value={exampleOutput}
                    onChange={(e) => setExampleOutput(e.target.value)}
                    placeholder="Paste an example of the desired output here. This helps the AI match the exact style, tone, and formatting..."
                    className="textarea h-40 font-mono text-sm"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            {/* Facts Data (Optional) */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Facts Data</h2>
                    <p className="text-gray-500">Optional: Provide facts for accurate content generation</p>
                  </div>
                </div>

                {/* Facts Source Selection */}
                <div className="mb-4">
                  <label className="label mb-2">Facts Source</label>
                  <div className="flex items-center gap-4 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="factsSource"
                        value="none"
                        checked={factsSource === 'none'}
                        onChange={() => {
                          setFactsSource('none');
                          setFactsFile(null);
                          setSelectedFactsJobId(null);
                        }}
                        className="w-4 h-4 text-blue-600"
                        disabled={isSubmitting}
                      />
                      <span className="text-sm text-gray-700">No facts</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="factsSource"
                        value="file"
                        checked={factsSource === 'file'}
                        onChange={() => {
                          setFactsSource('file');
                          setSelectedFactsJobId(null);
                        }}
                        className="w-4 h-4 text-blue-600"
                        disabled={isSubmitting}
                      />
                      <span className="text-sm text-gray-700">Upload File</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="factsSource"
                        value="factsagent"
                        checked={factsSource === 'factsagent'}
                        onChange={() => {
                          setFactsSource('factsagent');
                          setFactsFile(null);
                        }}
                        className="w-4 h-4 text-blue-600"
                        disabled={isSubmitting || factsAgentJobs.length === 0}
                      />
                      <span className={`text-sm ${factsAgentJobs.length === 0 ? 'text-gray-400' : 'text-gray-700'}`}>
                        From Facts Agent {factsAgentJobs.length > 0 && `(${factsAgentJobs.length} available)`}
                      </span>
                    </label>
                  </div>
                </div>

                {/* File Upload Option */}
                {factsSource === 'file' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <label className="label">
                      Upload Facts File
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="file"
                        accept=".txt,.md"
                        onChange={(e) => setFactsFile(e.target.files?.[0] || null)}
                        className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-lg file:border-0
                          file:text-sm file:font-semibold
                          file:bg-blue-50 file:text-blue-700
                          hover:file:bg-blue-100
                          cursor-pointer"
                        disabled={isSubmitting}
                      />
                      {factsFile && (
                        <button
                          onClick={() => setFactsFile(null)}
                          className="btn-ghost btn-sm text-danger-500"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {factsFile && (
                      <p className="text-sm text-blue-600 mt-2">
                        Selected: {factsFile.name} ({(factsFile.size / 1024).toFixed(1)} KB)
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Upload a .txt or .md file containing facts about the company, products, services, etc.
                    </p>
                  </div>
                )}

                {/* Facts Agent Selection Option */}
                {factsSource === 'factsagent' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <label className="label">
                      Select Facts Agent Job
                    </label>
                    {loadingFactsJobs ? (
                      <div className="flex items-center gap-2 py-4">
                        <span className="spinner"></span>
                        <span className="text-sm text-gray-500">Loading Facts Agent jobs...</span>
                      </div>
                    ) : factsAgentJobs.length === 0 ? (
                      <p className="text-sm text-gray-500 py-4">
                        No completed Facts Agent jobs found for this project.
                        <br />
                        <span className="text-xs">Run the Facts Agent first to extract facts from a transcript.</span>
                      </p>
                    ) : (
                      <select
                        value={selectedFactsJobId || ''}
                        onChange={(e) => setSelectedFactsJobId(e.target.value ? parseInt(e.target.value) : null)}
                        className="input mt-2"
                        disabled={isSubmitting}
                      >
                        <option value="">Select a Facts Agent job...</option>
                        {factsAgentJobs.map((job) => (
                          <option key={job.id} value={job.id}>
                            Job #{job.id} - {job.fact_count} facts
                            {job.completed_at && ` - ${new Date(job.completed_at).toLocaleDateString()}`}
                          </option>
                        ))}
                      </select>
                    )}
                    {selectedFactsJobId && (
                      <p className="text-sm text-blue-600 mt-2">
                        Facts from Job #{selectedFactsJobId} will be used for content generation.
                      </p>
                    )}
                  </div>
                )}

                {factsSource === 'none' && (
                  <p className="text-xs text-gray-500 mt-2">
                    No facts will be used. Content will be generated based only on the brief and example output.
                  </p>
                )}
              </div>
            </div>

            {/* Page Entries */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Pages to Generate</h2>
                      <p className="text-gray-500">Add titles and URLs for each page</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setInputMode(inputMode === 'manual' ? 'bulk' : 'manual')}
                      className="btn-secondary btn-sm"
                    >
                      {inputMode === 'manual' ? 'Bulk Paste' : 'Manual Entry'}
                    </button>
                  </div>
                </div>

                {inputMode === 'bulk' ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                      <h3 className="font-semibold text-teal-900 mb-2">Bulk Paste - Separate Titles and URLs</h3>
                      <p className="text-sm text-teal-700">Paste titles in the left box and URLs in the right box (one per line). They will be matched by line number.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Titles (one per line) <span className="text-danger-500">*</span></label>
                        <textarea
                          value={bulkTitles}
                          onChange={(e) => setBulkTitles(e.target.value)}
                          placeholder="Medical Detox in Austin, TX
Medical Detox in Dallas, TX
Medical Detox in Houston, TX"
                          className="textarea h-48 font-mono text-sm"
                          disabled={isSubmitting}
                        />
                        <p className="text-xs text-gray-500 mt-1">{bulkTitles.split('\n').filter(l => l.trim()).length} titles</p>
                      </div>
                      <div>
                        <label className="label">URLs (one per line)</label>
                        <textarea
                          value={bulkUrls}
                          onChange={(e) => setBulkUrls(e.target.value)}
                          placeholder="/medical-detox-austin-tx
/medical-detox-dallas-tx
/medical-detox-houston-tx"
                          className="textarea h-48 font-mono text-sm"
                          disabled={isSubmitting}
                        />
                        <p className="text-xs text-gray-500 mt-1">{bulkUrls.split('\n').filter(l => l.trim()).length} URLs</p>
                      </div>
                    </div>
                    <button
                      onClick={parseBulkPaste}
                      disabled={!bulkTitles.trim()}
                      className="btn-primary"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add {bulkTitles.split('\n').filter(l => l.trim()).length} Pages
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pages.map((page, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={page.title}
                            onChange={(e) => updatePage(index, 'title', e.target.value)}
                            placeholder="Page Title"
                            className="input"
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={page.url}
                            onChange={(e) => updatePage(index, 'url', e.target.value)}
                            placeholder="/url-slug"
                            className="input"
                            disabled={isSubmitting}
                          />
                        </div>
                        <button
                          onClick={() => removePage(index)}
                          disabled={pages.length === 1 || isSubmitting}
                          className="btn-ghost btn-sm text-danger-500 hover:text-danger-700"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={addPage}
                      disabled={isSubmitting}
                      className="btn-secondary w-full"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Another Page
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !briefContent.trim() || validPagesCount === 0}
                className="btn-primary"
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    Continue to Settings
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                )}
              </button>
            </div>
            </>
            )}
          </div>
        )}

        {/* Configure Step */}
        {step === 'configure' && (
          <div className="max-w-3xl mx-auto">
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">Generation Settings</h2>
                <p className="text-sm text-gray-500 mt-1">Configure parallel processing and QA validation</p>
              </div>
              <div className="card-body space-y-6">
                {/* Parallel Threads */}
                <div>
                  <label className="label">Parallel Threads</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="8"
                      value={maxThreads}
                      onChange={(e) => setMaxThreads(parseInt(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-lg font-semibold text-gray-900 w-8">{maxThreads}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Number of articles to generate simultaneously</p>
                </div>

                {/* QA Threshold */}
                <div>
                  <label className="label">QA Threshold (%)</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="80"
                      max="100"
                      value={qaThreshold}
                      onChange={(e) => setQaThreshold(parseInt(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-lg font-semibold text-gray-900 w-12">{qaThreshold}%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Articles below this score will be regenerated</p>
                </div>

                {/* Max Retries */}
                <div>
                  <label className="label">Max Retries</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="0"
                      max="5"
                      value={maxRetries}
                      onChange={(e) => setMaxRetries(parseInt(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-lg font-semibold text-gray-900 w-8">{maxRetries}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Maximum regeneration attempts for failed QA</p>
                </div>

                {/* Info Box */}
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <h3 className="font-semibold text-purple-900 mb-2">QA Validation Criteria (100 points)</h3>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li>Brief Compliance: 30 points — structure, sections, required elements</li>
                    <li>Content Quality: 30 points — writing clarity, specificity, expert voice</li>
                    <li>SEO Effectiveness: 20 points — keyword integration, headers, semantic coverage</li>
                    <li>Formatting & Readability: 20 points — markdown, visual hierarchy, scannability</li>
                  </ul>
                </div>
              </div>
              <div className="card-footer flex justify-between">
                <button onClick={() => setStep('input')} className="btn-secondary">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <button onClick={handleGenerate} className="btn-primary" disabled={isGenerating}>
                  {isGenerating ? (
                    <>
                      <div className="spinner w-4 h-4"></div>
                      Starting...
                    </>
                  ) : (
                    <>
                      Start Generation
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Processing Step */}
        {step === 'processing' && jobId && (
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <div className="card-body">
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Generating Content</h2>
                    <p className="text-gray-500">Processing {validPagesCount} pages...</p>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await api.post(`/briefcontent/${jobId}/cancel`);
                        setError('Job cancellation requested...');
                      } catch (err: any) {
                        setError(err.response?.data?.detail || 'Failed to cancel job');
                      }
                    }}
                    className="btn-secondary text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Cancel
                  </button>
                </div>
                <ProgressMonitor
                  jobId={jobId}
                  systemType="briefcontent"
                  onStatusUpdate={async (status) => {
                    if (status.status === 'completed' || status.status === 'cancelled' || status.status === 'failed') {
                      try {
                        const response = await api.get(`/briefcontent/results/${jobId}`);
                        setResults(response.data);
                        setError(null);
                        setStep('results');
                        if (status.status === 'cancelled') {
                          setError('Job was cancelled');
                        } else if (status.status === 'failed') {
                          setError('Job failed: ' + (status.error_message || 'Unknown error'));
                        }
                      } catch (err) {
                        console.error('Failed to load results:', err);
                        setStep('results');
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Results Step */}
        {step === 'results' && jobId && results && (
          <div className="max-w-6xl mx-auto">
            {/* Stats Header */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="card">
                <div className="card-body">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-success-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{results.qa_summary.qa_passed}</p>
                      <p className="text-sm text-gray-500">QA Passed</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-warning-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-warning-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{results.qa_summary.qa_failed}</p>
                      <p className="text-sm text-gray-500">QA Failed</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{Math.round(results.qa_summary.average_score)}%</p>
                      <p className="text-sm text-gray-500">Avg QA Score</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">
                        {Math.round(results.generation_stats.average_time_seconds)}s
                      </p>
                      <p className="text-sm text-gray-500">Avg Time/Page</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Filter */}
            {Object.keys(results.by_status).length > 1 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-500">Filter by status:</span>
                  <button
                    onClick={() => setStatusFilter(null)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                      statusFilter === null
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All ({results.total_pages})
                  </button>
                  {Object.entries(results.by_status).map(([status, count]) => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        statusFilter === status
                          ? 'bg-purple-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {status} ({count})
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Pages Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {filteredPages.map((page) => (
                <div
                  key={page.id}
                  className="card hover:shadow-lg hover:border-brand-200 transition-all cursor-pointer group"
                  onClick={() => page.status === 'generated' && openEditor(page)}
                >
                  <div className="card-body">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 group-hover:text-brand-600 transition-colors truncate">
                          {page.title}
                        </h4>
                        {page.url && (
                          <p className="text-xs text-gray-400 mt-1 truncate">{page.url}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            page.status === 'generated'
                              ? 'bg-success-100 text-success-800'
                              : page.status === 'failed'
                              ? 'bg-danger-100 text-danger-800'
                              : page.status === 'processing'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {page.status}
                          </span>
                          {page.qa_score !== null && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              page.qa_passed
                                ? 'bg-success-100 text-success-800'
                                : 'bg-warning-100 text-warning-800'
                            }`}>
                              QA: {page.qa_score}%
                            </span>
                          )}
                          {page.retry_count > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              {page.retry_count} retries
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                          {page.word_count && (
                            <span>{page.word_count.toLocaleString()} words</span>
                          )}
                          {page.processing_time_seconds && (
                            <span>{page.processing_time_seconds}s</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                      {page.status === 'generated' ? (
                        <span className="text-brand-600 text-sm font-medium inline-flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          View/Edit
                        </span>
                      ) : page.status === 'failed' ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRegenerate(page.id);
                          }}
                          className="text-brand-600 text-sm font-medium inline-flex items-center gap-1 hover:text-brand-700"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Regenerate
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm">Processing...</span>
                      )}
                      {page.status === 'generated' && (
                        <a
                          href={`/api/briefcontent/pages/${page.id}/download?token=${localStorage.getItem('auth_token') || ''}`}
                          download
                          onClick={(e) => e.stopPropagation()}
                          className="btn-ghost btn-sm"
                          title="Download"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Job Management */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Job Management</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Job ID: {results.job.id} - {results.total_pages} pages
                      {results.job.completed_at && ` - Completed: ${new Date(results.job.completed_at).toLocaleString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <a
                      href={`/api/briefcontent/${jobId}/download-all?token=${localStorage.getItem('auth_token') || ''}`}
                      download
                      className="btn-primary"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download All (ZIP)
                    </a>
                    <button onClick={handleReset} className="btn-secondary">
                      New Job
                    </button>
                    <button onClick={handleDelete} className="btn-danger">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Job
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content Editor Modal */}
      {editorOpen && editingPage && (
        <BriefContentEditorModal
          isOpen={editorOpen}
          onClose={closeEditor}
          onSave={handleEditorSave}
          pageId={editingPage.id}
          pageTitle={editingPage.title}
        />
      )}
    </Layout>
  );
}
